import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getDelayedHeadlines } from "./api";
import { useMemo, useEffect, useState, useRef } from "react";
import type {
  Headline,
  HeadlineTimestamp,
} from "@/widget-components/newsquawk-headlines/models/Headline";
import headlinesSSEClient from "./headlinesSSEClient";
import type { ConnectionStatus } from "@/shared/services/configurable-sse-client";
import {
  shouldHideHeadline,
  filterVisibleHeadlines,
} from "./headlineVisibility";

/**
 * Gets the Unix timestamp from a headline's published_at field.
 * Handles both HeadlineTimestamp object and ISO string formats.
 */
const getUnixTimestamp = (publishedAt: HeadlineTimestamp | string): number => {
  if (typeof publishedAt === "string") {
    return Date.parse(publishedAt) / 1000;
  }
  return publishedAt.unix;
};

/** Maximum headlines to keep in the first page of the infinite query */
const MAX_FIRST_PAGE = 200;

interface UseDelayedHeadlinesOptions {
  subscribe?: boolean;
}

export const useDelayedHeadlines = (options?: UseDelayedHeadlinesOptions) => {
  const { subscribe = true } = options || {};
  const queryClient = useQueryClient();

  const [subscribeStatus, setSubscribeStatus] =
    useState<ConnectionStatus>("inactive");

  const delayedHeadlinesQuery = useInfiniteQuery({
    queryKey: ["delayed-headlines"],
    queryFn: ({ pageParam }) => getDelayedHeadlines({ cursor: pageParam }),
    getNextPageParam: (lastPage) => {
      // Return the next cursor if there are more pages
      return lastPage.pagination.has_more
        ? lastPage.pagination.next_cursor
        : undefined;
    },
    initialPageParam: undefined as string | undefined,

    // Only fetch delayed headlines for users without live access — authed
    // live users never display this feed. Mirrors the SSE subscription gate.
    enabled: subscribe,

    // refetch configs
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Flatten all pages into a single array of headlines
  const headlines = useMemo(() => {
    if (!delayedHeadlinesQuery.data?.pages) return [];
    return delayedHeadlinesQuery.data.pages.reduce((acc: Headline[], page) => {
      // Matches the SSE handler below — hidden headlines are dropped on both
      // transports, not just the live one.
      return [...acc, ...filterVisibleHeadlines(page.data)];
    }, []);
  }, [delayedHeadlinesQuery.data]);

  // Get the last page's pagination info
  const pagination = useMemo(() => {
    if (!delayedHeadlinesQuery.data?.pages) return undefined;
    const lastPage =
      delayedHeadlinesQuery.data.pages[delayedHeadlinesQuery.data.pages.length - 1];
    return lastPage?.pagination;
  }, [delayedHeadlinesQuery.data]);

  const hasMore = useMemo(() => {
    return pagination?.has_more ?? false;
  }, [pagination]);

  const loadNextPage = () => {
    if (hasMore && !delayedHeadlinesQuery.isFetchingNextPage) {
      delayedHeadlinesQuery.fetchNextPage();
    }
  };

  /**
   * Handle incoming headline messages from SSE
   * Subscribe to the "headline-delayed" topic (no auth required)
   */
  useEffect(() => {
    if (!subscribe) return;

    const messageHandler = (data: Headline) => {
      if (data) {
        try {
          console.log("Received delayed headline via SSE:", data);

          const newHeadline = data as Headline;

          // Filter out hidden headlines using the shouldHideHeadline function
          if (shouldHideHeadline(newHeadline)) {
            return;
          }

          // Attach metadata
          newHeadline.isNew = true;
          newHeadline.received_at = Date.now();

          // Update the query cache by prepending the new headline to the first page
          queryClient.setQueryData(
            ["delayed-headlines"],
            (oldData: InfiniteData<{ data: Headline[] }>) => {
              if (!oldData) return oldData;

              // Check if headline already exists in any page
              const headlineExists = oldData.pages.some((page) =>
                page.data.some((h: Headline) => h.id === newHeadline.id)
              );

              if (headlineExists) {
                // Update existing headline
                console.log("Updating existing delayed headline via SSE:", newHeadline);
                return {
                  ...oldData,
                  pages: oldData.pages.map((page) => ({
                    ...page,
                    data: page.data.map((h: Headline) =>
                      h.id === newHeadline.id ? newHeadline : h
                    ),
                  })),
                };
              }

              // Insert new headline at correct chronological position in first page
              const newTimestamp = getUnixTimestamp(newHeadline.published_at);
              const firstPage = oldData.pages[0];

              // Find correct position based on published_at (descending order - newest first)
              const insertIndex = firstPage.data.findIndex(
                (h: Headline) => getUnixTimestamp(h.published_at) < newTimestamp
              );

              // If insertIndex is -1, headline is older than all in first page
              // Insert at end of first page (or could belong in a later page, but we keep it here for simplicity)
              const insertAt = insertIndex === -1 ? firstPage.data.length : insertIndex;

              const newFirstPageData = [
                ...firstPage.data.slice(0, insertAt),
                newHeadline,
                ...firstPage.data.slice(insertAt),
              ];

              return {
                ...oldData,
                pages: oldData.pages.map((page, index: number) =>
                  index === 0
                    ? {
                        ...page,
                        data: newFirstPageData.length > MAX_FIRST_PAGE
                          ? newFirstPageData.slice(0, MAX_FIRST_PAGE)
                          : newFirstPageData,
                      }
                    : page
                ),
              };
            }
          );
        } catch (error) {
          console.error("Error processing delayed headline:", error);
        }
      }
    };

    const sub1 = headlinesSSEClient.subscribeTopic({
      topic: "headline-delayed",
      callback: messageHandler,
    });

    const sub2 = headlinesSSEClient.onStatusChange(setSubscribeStatus);

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [subscribe, queryClient]);

  return {
    headlines,
    pagination,
    hasMore,
    loadNextPage,
    loadingNextPage: delayedHeadlinesQuery.isFetchingNextPage,
    isLoading: delayedHeadlinesQuery.isLoading,
    isError: delayedHeadlinesQuery.isError,
    error: delayedHeadlinesQuery.error,
    subscribeStatus,
  };
};
